import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./ChangePasswordPage.css";

// サーバー（app/adminauth/password.go）の基準と揃える
const MIN_LENGTH = 12;

interface ChangePasswordPageProps {
  // true: 初回ログイン・管理者による再設定のあと（変更しないと先へ進めない）
  required?: boolean;
}

export const ChangePasswordPage: React.FC<ChangePasswordPageProps> = ({ required = false }) => {
  const { user, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if ([...newPassword].length < MIN_LENGTH) {
      setError(`パスワードは${MIN_LENGTH}文字以上にしてください`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("確認用のパスワードが一致しません");
      return;
    }
    try {
      setSubmitting(true);
      await changePassword(required ? "" : currentPassword, newPassword);
      setDone(true);
    } catch (err) {
      // 基準を満たさない理由（流出済み・推測されやすい など）はサーバーの文言をそのまま出す
      setError(err instanceof Error ? err.message : "パスワードを変更できませんでした");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className={required ? "change-password-container" : "change-password-inline"}>
        <div className="change-password-card">
          <div className="success-message">
            パスワードを変更しました。ほかの端末でログインしていた場合は、そちらはログアウトされています。
          </div>
          {!required && <Link to="/admin">管理画面に戻る</Link>}
        </div>
      </div>
    );
  }

  return (
    <div className={required ? "change-password-container" : "change-password-inline"}>
      <div className="change-password-card">
        <div className="change-password-header">
          <h1>{required ? "パスワードの変更が必要です" : "パスワード変更"}</h1>
          <p>
            {required && (
              <>
                初回ログイン（または管理者による再設定）のため、ご自身でパスワードを決めてください。
                <br />
              </>
            )}
            {MIN_LENGTH}文字以上で、ほかのサービスと使い回していないものにしてください。
            <br />
            「password」や社名に数字を足しただけのもの、過去に流出したパスワードは使えません。
            覚えやすい言葉をいくつかつなげた長めのパスワードがおすすめです。
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="change-password-form">
          <div className="form-group">
            <label htmlFor="email">メールアドレス</label>
            <input
              id="email"
              type="email"
              value={user?.email || ""}
              disabled
              className="disabled-input"
              autoComplete="username"
            />
          </div>

          {!required && (
            <div className="form-group">
              <label htmlFor="currentPassword">現在のパスワード</label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="newPassword">新しいパスワード</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={`${MIN_LENGTH}文字以上`}
              required
              autoComplete="new-password"
              minLength={MIN_LENGTH}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">新しいパスワード（確認）</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={MIN_LENGTH}
            />
          </div>

          <button type="submit" className="change-password-button" disabled={submitting}>
            {submitting ? "変更しています..." : "パスワードを変更"}
          </button>
        </form>
      </div>
    </div>
  );
};
